import { NavLink, Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useDarkMode from '../../hooks/useDarkMode';
import Logo from '../ui/Logo';
import LogoutButton from '../auth/LogoutButton';
import './layout.css';

/**
 * Admin nav items — add new admin pages here and they appear in the sidebar
 * automatically. Each item needs a path under /admin.
 */
const ADMIN_NAV = [
  { id: 'feedback', label: 'Feedback', icon: '📬', path: '/admin/feedback' },
];

/**
 * AdminLayout — wraps all /admin/* routes.
 * Uses the same CSS structure as AppLayout but with a minimal sidebar:
 * - Baby Tracker logo
 * - ← Back to app
 * - Admin page list
 * - Logout
 *
 * Auth gate: redirects to /app if not signed in or not an admin.
 */
export default function AdminLayout() {
  const { user, profile, loading: authLoading, profileLoading } = useAuth();
  const [isDark, toggleDark] = useDarkMode();

  if (authLoading || profileLoading) return null;
  if (!user)             return <Navigate to="/" replace />;
  if (!profile?.isAdmin) return <Navigate to="/app" replace />;

  return (
    <div className="app-layout" data-testid="admin-layout">
      {/* Admin sidebar */}
      <nav className="app-sidebar" aria-label="Admin navigation">
        <NavLink to="/app" className="sidebar-brand" aria-label="Baby Tracker home">
          <Logo variant="full" size={28} />
        </NavLink>

        <ul className="sidebar-nav">
          {/* Back to app — always first */}
          <li>
            <NavLink to="/app" className="sidebar-nav-item" end>
              <span className="sidebar-nav-icon" aria-hidden="true">←</span>
              Back to app
            </NavLink>
          </li>

          <li>
            <div style={{
              padding: '0.4rem 1.25rem',
              fontSize: '0.65rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.7px',
              color: 'var(--text-light)',
              marginTop: '0.25rem',
            }}>
              Admin
            </div>
          </li>

          {ADMIN_NAV.map((item) => (
            <li key={item.id}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `sidebar-nav-item${isActive ? ' active' : ''}`
                }
              >
                <span className="sidebar-nav-icon" aria-hidden="true">{item.icon}</span>
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="sidebar-footer">
          <LogoutButton />
        </div>
      </nav>

      {/* Main content */}
      <div className="app-main">
        <header className="app-top-bar">
          <NavLink to="/app" className="app-brand d-md-none" aria-label="Baby Tracker home">
            <Logo variant="full" size={26} />
          </NavLink>

          <span style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            color: 'var(--text-light)',
            marginLeft: '0.25rem',
          }}>
            Admin
          </span>

          <div className="app-top-bar-actions">
            <button
              className="app-top-bar-btn"
              onClick={toggleDark}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? '☀️' : '🌙'}
            </button>
          </div>
        </header>

        <main className="app-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
